import React, { useEffect, useState } from "react";
import { useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Alert,
  AlertIcon,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import { isSystemAdmin } from "./configHelpers";

// Create Cohort Form Component
function CreateCohortForm({ institution, onCohortCreated, enabled = true, session }) {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY
  const toast = useToast();
  const [{ fetching, error, data }, createCohort] = useGlobalAction(api.createCohortGlobal);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    cohortType: "EMT_Basic",
    academicYear: new Date().getFullYear() + "-" + (new Date().getFullYear() + 1),
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
    isActive: true,
    institution: decodeURIComponent(institution),
    shiftEligibility: {
      teamLeadEligible: false,
      instructorEligible: false,
      preceptorEligible: false,
      fieldInternshipEligible: false
    },
    assignedInstructors: [],
    clinicalDocumentationInstructors: [],
    enableClinicalDocNotifications: false,
    escalationMembers: [],
    escalationSettings: {
      autoEscalationDelay: 24,
      escalationReasons: ['Failed Audit', 'Missing Documentation', 'Student Issue', 'Other'],
      requiresEscalationApproval: false
    },
    instructorPermissions: {}
  });

  // Log action data changes for debugging
  useEffect(() => {
    if (data) {
      console.log('Global action response data:', data);
    }
  }, [data]);

  // Early return AFTER all hooks are declared
  if (!enabled) {
    return null;
  }

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = "Cohort name is required";
    if (!formData.displayName?.trim()) errors.displayName = "Display name is required";
    if (!formData.startDate) errors.startDate = "Start date is required";
    if (!formData.endDate) errors.endDate = "End date is required";
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      errors.endDate = "End date must be after start date";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const unwrapCohortCreateResult = (result) => {
    const payload =
      result?.data?.createCohortGlobal ||
      result?.data ||
      result?.createCohortGlobal ||
      result;

    const success = payload?.success === true || Boolean(payload?.cohort?.id);
    const cohort = payload?.cohort || null;
    const errorMessage =
      payload?.error ||
      payload?.message ||
      result?.error?.message ||
      result?.message ||
      "Failed to create cohort";

    return { success, cohort, errorMessage, payload };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Starting cohort creation process...');

    // Clear previous validation errors
    setValidationErrors({});

    // Validate form before submission
    if (!validateForm()) {
      console.log('Form validation failed:', validationErrors);
      toast({
        title: "Validation Error",
        description: "Please check the form for errors",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      console.log('Submitting cohort data:', formData);

      if (!session?.user?.id) {
        throw new Error("User session not found. Please refresh and try again.");
      }

      // Prepare data for the global action - pass fields directly (not wrapped in cohortData)
      const dataToSubmit = {
        // Basic info - pass directly to match the action params
        name: formData.name || `${formData.cohortType}_${new Date().getTime()}`,
        displayName: formData.displayName,
        cohortType: formData.cohortType,
        academicYear: formData.academicYear,
        // Convert date strings to proper DateTime format
        startDate: new Date(formData.startDate + 'T00:00:00.000Z').toISOString(),
        endDate: new Date(formData.endDate + 'T23:59:59.999Z').toISOString(),
        isActive: formData.isActive,

        // Institution info
        institution: decodeURIComponent(institution),

        // Optional fields
        maxStudents: formData.maxStudents ? parseInt(formData.maxStudents, 10) : undefined,
        shiftEligibility: formData.shiftEligibility,
        assignedInstructors: formData.assignedInstructors,
        clinicalDocumentationInstructors: formData.clinicalDocumentationInstructors,
        enableClinicalDocNotifications: formData.enableClinicalDocNotifications,
        escalationMembers: formData.escalationMembers,
        escalationSettings: formData.escalationSettings,
        instructorPermissions: formData.instructorPermissions,
      };

      console.log('Prepared data for global action:', dataToSubmit);

      const result = await createCohort(dataToSubmit);
      console.log('Global action response:', result);
      const normalized = unwrapCohortCreateResult(result);
      console.log('Normalized action response:', normalized);

      // Check if the action was successful
      if (normalized.success) {
        console.log('Cohort created successfully:', normalized.cohort);

        toast({
          title: "Cohort created",
          description: `New cohort "${normalized.cohort?.displayName || normalized.cohort?.name || "cohort"}" has been created. Open the cohort editor to set up semesters.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Reset form fields
        setFormData({
          ...formData,
          name: "",
          displayName: "",
          shiftEligibility: {
            teamLeadEligible: false,
            instructorEligible: false,
            preceptorEligible: false,
            fieldInternshipEligible: false
          },
          assignedInstructors: [],
          clinicalDocumentationInstructors: [],
          enableClinicalDocNotifications: false,
          escalationMembers: [],
          escalationSettings: {
            autoEscalationDelay: 24,
            escalationReasons: ['Failed Audit', 'Missing Documentation', 'Student Issue', 'Other'],
            requiresEscalationApproval: false
          },
          instructorPermissions: {}
        });

        // Refresh the cohort list immediately and again after a delay
        if (onCohortCreated) {
          onCohortCreated();
          setTimeout(() => onCohortCreated(), 500);
          setTimeout(() => onCohortCreated(), 1000);
        }
      } else {
        // Handle unsuccessful response with errors in the result
        console.error('Cohort creation failed:', normalized.payload);
        const errorMessage = normalized.errorMessage || "Failed to create cohort";

        toast({
          title: "Error creating cohort",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Exception during cohort creation:', err);
      toast({
        title: "Error creating cohort",
        description: err.message || "An unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleShiftEligibilityChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      shiftEligibility: {
        ...prev.shiftEligibility,
        [field]: value
      }
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
          <FormControl isRequired isInvalid={validationErrors.name}>
            <FormLabel color="white">Cohort Name (Internal ID)</FormLabel>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., EMT_Basic_Fall_2025"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _invalid={{ borderColor: "red.400" }}
            />
            {validationErrors.name && (
              <Text color="red.400" fontSize="xs" mt={1}>{validationErrors.name}</Text>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={validationErrors.displayName}>
            <FormLabel color="white">Display Name</FormLabel>
            <Input
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="e.g., EMT Basic - Fall 2025"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _invalid={{ borderColor: "red.400" }}
            />
            {validationErrors.displayName && (
              <Text color="red.400" fontSize="xs" mt={1}>{validationErrors.displayName}</Text>
            )}
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="100%">
          <FormControl isRequired>
            <FormLabel color="white">Cohort Type</FormLabel>
            <Select
              name="cohortType"
              value={formData.cohortType}
              onChange={handleChange}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
            >
              <option value="EMT_Basic">EMT Basic</option>
              <option value="EMT_Advanced">EMT Advanced</option>
              <option value="Paramedic">Paramedic</option>
              <option value="Nursing">Nursing</option>
              <option value="Field_Internship">Field Internship</option>
              <option value="Other">Other</option>
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel color="white">Academic Year</FormLabel>
            <Input
              name="academicYear"
              value={formData.academicYear}
              onChange={handleChange}
              placeholder="e.g., 2025-2026"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
            />
          </FormControl>

          <FormControl>
            <FormLabel color="white">Max Students</FormLabel>
            <NumberInput
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              min={1}
            >
              <NumberInputField
                name="maxStudents"
                value={formData.maxStudents}
                onChange={handleChange}
                placeholder="Unlimited"
              />
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
          <FormControl isRequired isInvalid={validationErrors.startDate}>
            <FormLabel color="white">Start Date</FormLabel>
            <Input
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _invalid={{ borderColor: "red.400" }}
            />
            {validationErrors.startDate && (
              <Text color="red.400" fontSize="xs" mt={1}>{validationErrors.startDate}</Text>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={validationErrors.endDate}>
            <FormLabel color="white">End Date</FormLabel>
            <Input
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleChange}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              _invalid={{ borderColor: "red.400" }}
            />
            {validationErrors.endDate && (
              <Text color="red.400" fontSize="xs" mt={1}>{validationErrors.endDate}</Text>
            )}
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel color="white">Shift Eligibility</FormLabel>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <HStack>
              <Switch
                isChecked={formData.shiftEligibility?.teamLeadEligible || false}
                onChange={(e) => handleShiftEligibilityChange('teamLeadEligible', e.target.checked)}
                colorScheme="purple"
                size="sm"
              />
              <Text fontSize="sm" color="white">Team Lead</Text>
            </HStack>
            <HStack>
              <Switch
                isChecked={formData.shiftEligibility?.instructorEligible || false}
                onChange={(e) => handleShiftEligibilityChange('instructorEligible', e.target.checked)}
                colorScheme="green"
                size="sm"
              />
              <Text fontSize="sm" color="white">Instructor</Text>
            </HStack>
            <HStack>
              <Switch
                isChecked={formData.shiftEligibility?.preceptorEligible || false}
                onChange={(e) => handleShiftEligibilityChange('preceptorEligible', e.target.checked)}
                colorScheme="orange"
                size="sm"
              />
              <Text fontSize="sm" color="white">Preceptor</Text>
            </HStack>
            <HStack>
              <Switch
                isChecked={formData.shiftEligibility?.fieldInternshipEligible || false}
                onChange={(e) => handleShiftEligibilityChange('fieldInternshipEligible', e.target.checked)}
                colorScheme="cyan"
                size="sm"
              />
              <Text fontSize="sm" color="white">Field Internship</Text>
            </HStack>
          </SimpleGrid>
        </FormControl>

        {isSystemAdmin(session) && (
          <FormControl>
            <HStack justifyContent="space-between">
              <FormLabel color="white">Enable Clinical Documentation Notifications</FormLabel>
              <Switch
                isChecked={formData.enableClinicalDocNotifications || false}
                onChange={(e) => handleChange({ target: { name: 'enableClinicalDocNotifications', type: 'checkbox', checked: e.target.checked }})}
                colorScheme="teal"
              />
            </HStack>
            <Text fontSize="sm" color="gray.400" mt={2}>
              When enabled, selected instructors will receive notifications from clinical document processing.
            </Text>
          </FormControl>
        )}

        {isSystemAdmin(session) && formData.enableClinicalDocNotifications && (
          <FormControl>
            <FormLabel color="white">Clinical Documentation Instructors</FormLabel>
            <Text fontSize="sm" color="gray.400" mb={2}>
              Select instructors who should receive clinical documentation notifications for this cohort.
            </Text>
            <Input
              placeholder="Instructor selection will be added here"
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              isReadOnly
            />
          </FormControl>
        )}

        <FormControl>
          <FormLabel color="white">Escalation Members</FormLabel>
          <Text fontSize="sm" color="gray.400" mb={2}>
            Select administrators or leaders who can receive escalated issues from FISDAP shift processing.
          </Text>
          <Input
            placeholder="Escalation member selection will be added here"
            bg="gray.700"
            borderColor="gray.600"
            color="white"
            isReadOnly
          />
        </FormControl>

        <FormControl>
          <FormLabel color="white">Auto-Escalation Delay (Hours)</FormLabel>
          <NumberInput
            value={formData.escalationSettings?.autoEscalationDelay || 24}
            onChange={(value) => {
              setFormData(prev => ({
                ...prev,
                escalationSettings: {
                  ...prev.escalationSettings,
                  autoEscalationDelay: parseInt(value) || 24
                }
              }));
            }}
            min={1}
            max={168}
            bg="gray.700"
            borderColor="gray.600"
            color="white"
          >
            <NumberInputField placeholder="24" />
          </NumberInput>
          <Text fontSize="sm" color="gray.400" mt={1}>
            Hours before issues are automatically escalated to administrators.
          </Text>
        </FormControl>

        <HStack width="100%" justify="space-between">
          <FormControl display="flex" alignItems="center">
            <Switch
              name="isActive"
              isChecked={formData.isActive}
              onChange={handleChange}
              colorScheme="green"
              mr={2}
            />
            <FormLabel color="white" mb={0}>Active Cohort</FormLabel>
          </FormControl>

          <Button
            type="button"
            colorScheme="blue"
            isLoading={fetching}
            loadingText="Creating..."
            onClick={async () => {
              console.log('Starting cohort creation process...');

              // Clear previous validation errors
              setValidationErrors({});

              // Validate form before submission
              if (!validateForm()) {
                console.log('Form validation failed:', validationErrors);
                toast({
                  title: "Validation Error",
                  description: "Please check the form for errors",
                  status: "error",
                  duration: 5000,
                  isClosable: true,
                });
                return;
              }

              try {
                console.log('Submitting cohort data:', formData);

                if (!session?.user?.id) {
                  throw new Error("User session not found. Please refresh and try again.");
                }

                // Prepare data for the global action - pass fields directly (not wrapped in cohortData)
                const dataToSubmit = {
                  // Basic info - pass directly to match the action params
                  name: formData.name || `${formData.cohortType}_${new Date().getTime()}`,
                  displayName: formData.displayName,
                  cohortType: formData.cohortType,
                  academicYear: formData.academicYear,
                  // Convert date strings to proper DateTime format
                  startDate: new Date(formData.startDate + 'T00:00:00.000Z').toISOString(),
                  endDate: new Date(formData.endDate + 'T23:59:59.999Z').toISOString(),
                  isActive: formData.isActive,

                  // Institution info
                  institution: decodeURIComponent(institution),

                  // Optional fields
                  maxStudents: formData.maxStudents ? parseInt(formData.maxStudents, 10) : undefined,
                  shiftEligibility: formData.shiftEligibility,
                  assignedInstructors: formData.assignedInstructors,
                  clinicalDocumentationInstructors: formData.clinicalDocumentationInstructors,
                  enableClinicalDocNotifications: formData.enableClinicalDocNotifications,
                  escalationMembers: formData.escalationMembers,
                  escalationSettings: formData.escalationSettings,
                  instructorPermissions: formData.instructorPermissions,
                };

                console.log('Prepared data for global action:', dataToSubmit);

                const result = await createCohort(dataToSubmit);
                console.log('Global action response:', result);
                const normalized = unwrapCohortCreateResult(result);
                console.log('Normalized action response:', normalized);

                // Check if the action was successful
                if (normalized.success) {
                  console.log('Cohort created successfully:', normalized.cohort);

                  toast({
                    title: "Cohort created",
                    description: `New cohort "${normalized.cohort?.displayName || normalized.cohort?.name || "cohort"}" has been created. Open the cohort editor to set up semesters.`,
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });

                  // Reset form fields
                  setFormData({
                    ...formData,
                    name: "",
                    displayName: "",
                    shiftEligibility: {
                      teamLeadEligible: false,
                      instructorEligible: false,
                      preceptorEligible: false,
                      fieldInternshipEligible: false
                    },
                    assignedInstructors: [],
                    clinicalDocumentationInstructors: [],
                    enableClinicalDocNotifications: false,
                    escalationMembers: [],
                    escalationSettings: {
                      autoEscalationDelay: 24,
                      escalationReasons: ['Failed Audit', 'Missing Documentation', 'Student Issue', 'Other'],
                      requiresEscalationApproval: false
                    },
                    instructorPermissions: {}
                  });

                  // Refresh the cohort list immediately and again after a delay
                  if (onCohortCreated) {
                    onCohortCreated();
                    setTimeout(() => onCohortCreated(), 500);
                    setTimeout(() => onCohortCreated(), 1000);
                  }
                } else {
                  // Handle unsuccessful response with errors in the result
                  console.error('Cohort creation failed:', normalized.payload);
                  const errorMessage = normalized.errorMessage || "Failed to create cohort";

                  toast({
                    title: "Error creating cohort",
                    description: errorMessage,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                  });
                }
              } catch (err) {
                console.error('Exception during cohort creation:', err);
                toast({
                  title: "Error creating cohort",
                  description: err.message || "An unknown error occurred",
                  status: "error",
                  duration: 5000,
                  isClosable: true,
                });
              }
            }}
          >
            Create Cohort
          </Button>

          <Button
            type="button"
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={async () => {
              console.log('Testing global action directly...');
              const testData = {
                name: "Test_Cohort_" + new Date().getTime(),
                displayName: "Test Cohort",
                cohortType: "EMT_Basic",
                academicYear: "2025-2026",
                startDate: new Date('2025-01-01T00:00:00.000Z').toISOString(),
                endDate: new Date('2025-12-31T23:59:59.999Z').toISOString(),
                isActive: true,
                institution: decodeURIComponent(institution),
              };
              console.log('Test data:', testData);
              try {
                const result = await createCohort(testData);
                console.log('Test result:', result);
                alert('Check console for test result');
              } catch (err) {
                console.error('Test error:', err);
                alert('Test failed - check console');
              }
            }}
          >
            Debug Test
          </Button>
        </HStack>

        {/* Display validation errors */}
        {Object.keys(validationErrors).length > 0 && (
          <Alert status="warning" mt={4}>
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Please fix the following errors:</Text>
              <VStack align="start" mt={2} spacing={1}>
                {Object.entries(validationErrors).map(([field, message]) => (
                  <Text key={field} fontSize="sm">{message}</Text>
                ))}
              </VStack>
            </Box>
          </Alert>
        )}

        {/* Display action error */}
        {error && (
          <Alert status="error" mt={4}>
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Error creating cohort</Text>
              <Text fontSize="sm">{error.message || "Unknown error"}</Text>
              <Text fontSize="xs" mt={1}>Please check the form and try again.</Text>
              {error.errors && error.errors.length > 0 && (
                <VStack align="start" mt={2} spacing={1}>
                  <Text fontSize="xs" fontWeight="bold">Details:</Text>
                  {error.errors.map((err, idx) => (
                    <Text key={idx} fontSize="xs">{err.message}</Text>
                  ))}
                </VStack>
              )}
            </Box>
          </Alert>
        )}

        {/* Display response errors if operation failed */}
        {data && !data.success && data.errors && data.errors.length > 0 && (
          <Alert status="error" mt={4}>
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Error from server</Text>
              <VStack align="start" mt={2} spacing={1}>
                {data.errors.map((err, idx) => (
                  <Text key={idx} fontSize="sm">{err.message}</Text>
                ))}
              </VStack>
              <Text fontSize="xs" mt={2}>Action ID: {data.actionRun}</Text>
            </Box>
          </Alert>
        )}
      </VStack>
    </form>
  );
}

export default CreateCohortForm;
